import { SAMPLE_RATE, SAMPLE_SETS, SAMPLE_SOUNDS, type SampleFile, type SampleSetId, type SampleSound } from "../assets/sounds.generated.ts";
import { limiter, sniff, type Fetch, type SampleFormat } from "./audio-fetch.ts";
import type { SoundId } from "./recipes.ts";

/**
 * Amostras dos efeitos (F45, ADR 0009): os arquivos que `tools/assets` grava em `public/sons`
 * (Opus em WebM, com MP3 de reserva), baixados e decodificados uma vez por página. Um som com
 * amostra toca a amostra; sem ela (rede, arquivo inválido, decodificador, prazo), a síntese
 * continua tocando: amostras nunca são necessárias.
 *
 * - Os downloads começam já, durante o pré-render da síntese, até 6 ao mesmo tempo; a decodificação
 *   só depois de `gate` (o fim do pré-render): o WebKit cai decodificando durante um render offline.
 * - Um prazo só (15 s), contado do fim do pré-render (a espera do render não come o prazo), com
 *   `abort`; `load()` nunca rejeita. Pior caso: o render mais 15 s.
 * - Confere os bytes mágicos (o fallback de SPA devolve o index.html com 200).
 * - O primeiro WebM que chega decide o formato: se não decodifica, o banco todo vai para o MP3
 *   (sem olhar o navegador; os WebM ainda na fila não baixam).
 * - Corta cada amostra no tamanho gravado (`frames`): o que sobrar é atraso ou enchimento do
 *   codificador. O atraso sai do começo pela diferença entre o início medido (`energyOnset`) e o
 *   gravado (`onset`): o WebKit deixa 12 ms de atraso no MP3, e o pré-eco de um ataque forte o
 *   esconde de um corte por silêncio.
 * - Uma variação que falha não derruba as outras.
 */

/** Prazo do carregamento, do fim do pré-render (o servidor expulsa quem não confirma o carregamento em 30 s). */
export const SAMPLE_LOAD_TIMEOUT_MS = 15_000;
/** Downloads ao mesmo tempo. */
export const SAMPLE_CONCURRENCY = 6;
/** Início de um som: a fração da energia que marca o `onset` gravado (a mesma do pipeline). */
const ONSET_ENERGY = 0.02;

export interface SampleDecoder {
  decode(bytes: ArrayBuffer): Promise<AudioBuffer>;
  /** Buffer mono novo com estas amostras. */
  buffer(data: Float32Array, rate: number): AudioBuffer;
}

export interface SampleStats {
  state: "parado" | "carregando" | "pronto";
  /** Formato em uso; `nenhum` sem WebAudio. */
  format: SampleFormat | "nenhum";
  /**
   * Carregamento inteiro (com a espera do pré-render) e o trecho das decodificações, do começo da
   * primeira ao fim da última (somar as durações contaria a fila: decodificam ao mesmo tempo).
   */
  ms: number;
  decodeMs: number;
  /** Bytes baixados das amostras decodificadas e o PCM delas (float32). */
  bytes: number;
  pcmBytes: number;
  expected: number;
  decoded: number;
  failed: number;
  error?: string;
}

/** O que um som toca: as variações decodificadas e o tom, o filtro e o ganho da hora de tocar. */
export interface ReadySample {
  buffers: readonly AudioBuffer[];
  gain: number;
  rate: number;
  lowpass?: number;
  loop?: boolean;
  /** Loop com trecho (o fogo, F46): o que repete na primeira variação, em segundos. */
  loopStart?: number;
  loopEnd?: number;
}

/** Amostras até chegar 2 % da energia (o mesmo `energyOnset` do pipeline, `tools/assets/src/sounds/pcm.ts`). */
export function energyOnset(data: Float32Array, fraction = ONSET_ENERGY): number {
  let total = 0;
  for (let i = 0; i < data.length; i++) total += data[i]! * data[i]!;
  let acc = 0;
  let i = 0;
  while (i < data.length && acc < total * fraction) {
    acc += data[i]! * data[i]!;
    i++;
  }
  return i;
}

/**
 * Trecho a guardar: `frames` amostras. O que sobra no começo é o quanto o início medido passou do
 * gravado (`onset`), até o excesso; o resto do excesso é enchimento no fim.
 */
export function fitFrames(data: Float32Array, frames: number, onset: number): { start: number; length: number } {
  const excess = data.length - frames;
  if (frames <= 0 || excess <= 0) return { start: 0, length: data.length };
  return { start: Math.max(0, Math.min(excess, energyOnset(data) - onset)), length: frames };
}

/** Decodificador do navegador: um contexto offline a 48 kHz (sem gesto; a taxa das amostras). */
function browserDecoder(): SampleDecoder | undefined {
  if (typeof OfflineAudioContext === "undefined") return undefined;
  let ctx: OfflineAudioContext | undefined;
  const context = () => (ctx ??= new OfflineAudioContext(1, 1, SAMPLE_RATE));
  return {
    decode: (bytes) => context().decodeAudioData(bytes),
    buffer: (data, rate) => {
      const b = context().createBuffer(1, data.length, rate);
      b.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
      return b;
    },
  };
}

interface Job {
  set: SampleSetId;
  index: number;
  file: SampleFile;
}

type Outcome = "ok" | "decodificador" | "falhou";

export class SampleBank {
  private readonly fetch: Fetch;
  private readonly decoder: SampleDecoder | undefined;
  private readonly timeoutMs: number;
  private readonly sets: Readonly<Record<string, readonly SampleFile[]>>;
  private readonly sounds: Readonly<Partial<Record<SoundId, SampleSound>>>;
  private readonly now: () => number;
  private pending: Promise<void> | undefined;
  private decodedSets = new Map<string, (AudioBuffer | undefined)[]>();
  private ready = new Map<SoundId, ReadySample>();
  private closed = false;
  private decodeSpan: { start: number; end: number } | undefined;
  private status: SampleStats;

  constructor(
    o: {
      fetch?: Fetch;
      decoder?: SampleDecoder | undefined;
      timeoutMs?: number;
      sets?: Readonly<Record<string, readonly SampleFile[]>>;
      sounds?: Readonly<Partial<Record<SoundId, SampleSound>>>;
      now?: () => number;
    } = {},
  ) {
    this.fetch = o.fetch ?? ((url, init) => globalThis.fetch(url, init));
    this.decoder = "decoder" in o ? o.decoder : browserDecoder();
    this.timeoutMs = o.timeoutMs ?? SAMPLE_LOAD_TIMEOUT_MS;
    this.sets = o.sets ?? SAMPLE_SETS;
    this.sounds = o.sounds ?? SAMPLE_SOUNDS;
    this.now = o.now ?? (() => performance.now());
    const expected = Object.values(this.sets).reduce((n, files) => n + files.length, 0);
    this.status = { state: "parado", format: this.decoder ? "webm" : "nenhum", ms: 0, decodeMs: 0, bytes: 0, pcmBytes: 0, expected, decoded: 0, failed: 0 };
  }

  /** Baixa e decodifica uma vez por página (idempotente; nunca rejeita). Decodifica depois de `gate`. */
  load(gate: Promise<unknown> = Promise.resolve()): Promise<void> {
    this.pending ??= this.run(gate);
    return this.pending;
  }

  /** As variações prontas de um som (sem nenhuma: a síntese). */
  get(id: SoundId): ReadySample | undefined {
    return this.ready.get(id);
  }

  stats(): SampleStats {
    return { ...this.status };
  }

  private async run(gate: Promise<unknown>): Promise<void> {
    const start = this.now();
    if (!this.decoder) {
      this.status = { ...this.status, state: "pronto", failed: this.status.expected, error: "sem WebAudio" };
      return;
    }
    this.status.state = "carregando";
    const abort = new AbortController();
    const jobs: Job[] = [];
    for (const [set, files] of Object.entries(this.sets)) files.forEach((file, index) => jobs.push({ set: set as SampleSetId, index, file }));
    // Os downloads não esperam o render: começam já, até 6 ao mesmo tempo.
    const fetchSlot = limiter(SAMPLE_CONCURRENCY);
    const downloadAll = (format: SampleFormat) => jobs.map((job) => fetchSlot(() => this.download(job, format, abort.signal)));
    const webm = downloadAll("webm");
    await gate;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(() => {
        this.status.error ??= `tempo esgotado (${this.timeoutMs} ms)`;
        this.closed = true;
        abort.abort();
        resolve();
      }, this.timeoutMs);
    });
    const work = this.decodeAll(jobs, webm, downloadAll);
    // Depois do prazo, o trabalho abortado ainda pode rejeitar: sem rejeição solta.
    work.catch(() => undefined);
    try {
      await Promise.race([work, timeout]);
    } catch (error) {
      this.status.error ??= String(error);
    } finally {
      clearTimeout(timer);
      this.closed = true;
    }
    this.publish();
    const decodeMs = this.decodeSpan ? Math.round(this.decodeSpan.end - this.decodeSpan.start) : 0;
    this.status = { ...this.status, state: "pronto", ms: Math.round(this.now() - start), decodeMs, failed: this.status.expected - this.status.decoded };
    if (this.status.decoded < this.status.expected) console.warn(`amostras: ${this.status.failed} de ${this.status.expected} indisponíveis; esses sons ficam sintetizados`, this.status.error ?? "");
  }

  /** Depois do portão: o primeiro WebM que chega decide o formato; o resto decodifica até 6 ao mesmo tempo. */
  private async decodeAll(jobs: readonly Job[], webm: readonly Promise<ArrayBuffer | undefined>[], downloadAll: (format: SampleFormat) => Promise<ArrayBuffer | undefined>[]): Promise<void> {
    let downloads = webm;
    let next = 0;
    while (next < jobs.length && !this.closed) {
      const bytes = await downloads[next];
      if (!bytes) {
        next++;
        continue;
      }
      const outcome = await this.decodeOne(jobs[next]!, bytes);
      if (outcome === "decodificador") {
        // O banco todo vai para o MP3, inclusive o que falhou ao baixar em WebM.
        this.status.format = "mp3";
        downloads = downloadAll("mp3");
        next = 0;
      } else next++;
      break;
    }
    const decodeSlot = limiter(SAMPLE_CONCURRENCY);
    await Promise.all(
      jobs.slice(next).map((job, i) =>
        decodeSlot(async () => {
          const bytes = await downloads[next + i];
          if (bytes && !this.closed) await this.decodeOne(job, bytes);
        }),
      ),
    );
  }

  /** Baixa e confere os bytes (sem rejeitar: falha vira `undefined`). Pula o formato que saiu de uso. */
  private async download(job: Job, format: SampleFormat, signal: AbortSignal): Promise<ArrayBuffer | undefined> {
    if (this.closed || format !== this.status.format) return undefined;
    const url = job.file[format];
    try {
      const response = await this.fetch(url, { signal });
      if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
      const bytes = await response.arrayBuffer();
      if (sniff(bytes) !== format) throw new Error(`${url}: não é ${format}`);
      return bytes;
    } catch (error) {
      this.status.error ??= String(error);
      return undefined;
    }
  }

  private async decodeOne(job: Job, bytes: ArrayBuffer): Promise<Outcome> {
    if (this.closed) return "falhou";
    const url = job.file[this.status.format as SampleFormat];
    // O decodificador do Chromium desanexa o ArrayBuffer: o tamanho vem antes.
    const size = bytes.byteLength;
    const t0 = this.now();
    let decoded: AudioBuffer;
    try {
      decoded = await this.decoder!.decode(bytes);
    } catch (error) {
      this.status.error ??= `${url}: ${String(error)}`;
      return "decodificador";
    } finally {
      this.decodeSpan = { start: Math.min(this.decodeSpan?.start ?? t0, t0), end: this.now() };
    }
    if (this.closed) return "falhou";
    const data = decoded.getChannelData(0);
    const { start, length } = fitFrames(data, job.file.frames, job.file.onset);
    const buffer = start === 0 && length === decoded.length && decoded.numberOfChannels === 1 ? decoded : this.decoder!.buffer(data.slice(start, start + length), decoded.sampleRate);
    const list = this.decodedSets.get(job.set) ?? [];
    list[job.index] = buffer;
    this.decodedSets.set(job.set, list);
    this.status.decoded++;
    this.status.bytes += size;
    this.status.pcmBytes += buffer.length * 4;
    return "ok";
  }

  /** Monta o que cada som toca com as variações que chegaram. */
  private publish(): void {
    for (const [id, sound] of Object.entries(this.sounds) as [SoundId, SampleSound][]) {
      const list = this.decodedSets.get(sound.set) ?? [];
      const buffers = list.filter((b): b is AudioBuffer => b !== undefined);
      if (!buffers.length) continue;
      // Os pontos de loop são do arquivo da primeira variação que chegou (a que o loop toca).
      const file = this.sets[sound.set]?.[list.findIndex((b) => b !== undefined)];
      const loop = file?.loopStart !== undefined && file.loopEnd !== undefined ? { loopStart: file.loopStart, loopEnd: file.loopEnd } : undefined;
      this.ready.set(id, { buffers, gain: sound.gain, rate: sound.rate, ...(sound.lowpass !== undefined && { lowpass: sound.lowpass }), ...(sound.loop && { loop: true }), ...loop });
    }
  }
}
